import { updateReapproAlgorithmParams, useReapproAlgorithmParams } from '@gozoki/api'
import {
  REAPPRO_ALGORITHM_BY_ID,
  REAPPRO_ALGORITHM_IDS,
  type ReapproAlgorithmParamUpdate,
} from '@gozoki/api-types'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { parseIntOrNull } from '../../../utils/types/number'
import { useToast } from '../../communication/Toast'

interface ReapproAlgorithmParamsPanelProps {
  isUserAdmin: boolean
}

const ReapproAlgorithmParamsPanel = ({ isUserAdmin }: ReapproAlgorithmParamsPanelProps) => {
  const [Toast, showToast] = useToast()
  const queryClient = useQueryClient()
  const { data: algoParams = [] } = useReapproAlgorithmParams()

  useEffect(() => {
    setParams(
      algoParams.map((param) => ({
        id: REAPPRO_ALGORITHM_IDS[param.algorithm],
        daysBetweenDeliveries: param.daysBetweenDeliveries,
      })),
    )
  }, [algoParams])

  const [params, setParams] = useState<ReapproAlgorithmParamUpdate[]>([])

  const updateAlgoDaysMut = useMutation({
    mutationFn: updateReapproAlgorithmParams,
    onSuccess: () => {
      showToast({
        message: 'Paramétrage réappro global mis à jour',
        severity: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['reapproAlgorithmParams'] })
    },
    onError: () => {
      showToast({
        message: "Impossible d'enregistrer le paramétrage réappro global",
        severity: 'error',
      })
    },
  })

  const algoDaysDisabled = useMemo(
    () => !isUserAdmin || updateAlgoDaysMut.isPending,
    [isUserAdmin, updateAlgoDaysMut.isPending],
  )

  const handleSaveAlgoDays = useCallback(() => {
    updateAlgoDaysMut.mutate(params)
  }, [params, updateAlgoDaysMut])

  return (
    <div style={{ padding: '16px' }}>
      <Toast />
      <Typography fontWeight={600} marginBottom="8px">
        Paramétrage fenêtre de vente pour réappro
      </Typography>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        {[
          REAPPRO_ALGORITHM_IDS.VMAX,
          REAPPRO_ALGORITHM_IDS.VMJ,
          REAPPRO_ALGORITHM_IDS.STOCK_PROJETE,
        ].map((algo) => (
          <TextField
            variant="outlined"
            size="small"
            label={REAPPRO_ALGORITHM_BY_ID[algo]}
            value={params.find((param) => param.id === algo)?.daysBetweenDeliveries}
            onChange={(e) =>
              setParams((prev) => [
                ...prev.filter((param) => param.id !== algo),
                {
                  id: algo,
                  daysBetweenDeliveries: parseIntOrNull(e.target.value),
                },
              ])
            }
            type="number"
            inputProps={{ min: 1, step: 1 }}
          />
        ))}
        <Button variant="contained" disabled={algoDaysDisabled} onClick={handleSaveAlgoDays}>
          Enregistrer
        </Button>
      </div>
      <Accordion sx={{ marginTop: '16px', boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ChevronDown />}>
          <Typography fontWeight={600}>Formules des algorithmes</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" gutterBottom>
            VMAX
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
            Besoin = max(0, ⌈VMA × TauxCouverture × Croissance - StockHorsDLC⌉)
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            VMJ
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
            Besoin = max(0, ⌈VMJ × TauxCouverture × Croissance - StockHorsDLC⌉)
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Stock Projeté
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 2 }}>
            Prévision = VMJ × TauxCouverture
            <br />
            StockAppel = max(Prévision, StockDLCCourte)
            <br />
            StockProjetéJ = StockHorsDLC - StockAppel
            <br />
            Besoin = max(0, ⌈Prévision × Croissance - StockProjetéJ⌉)
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            Légende
          </Typography>
          <Typography variant="body2" color="text.secondary">
            VMA = Ventes maximales journalières sur la fenêtre de vente
            <br />
            VMJ = Ventes moyennes journalières sur la fenêtre de vente
            <br />
            TauxCouverture = Taux de couverture de production du produit
            <br />
            Croissance = Taux de croissance du magasin
            <br />
            StockHorsDLC = Stock actuel − Stock DLC courte
            <br />
            StockDLCCourte = Stock à DLC courte (périmé ou bientôt périmé)
          </Typography>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

export default ReapproAlgorithmParamsPanel
